import '@assets/styles/pages/one-to-one-meetings.scss';
import { Dropdown, SingleDatePicker, SubmitButton, TextInput } from '@components/common';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	formatDateTime,
	IOneToOneMeetingFormInitialValues,
	oneToOneMeetingFormInitialValues,
	typeOfActivitiesList,
} from '.';
import { maxFileSize } from '@config/constant';
import { useGetRxDb } from '@hooks/getRxdbData';
import { TownList } from '@services/hooks/enduser-details';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { IDealerVisitProducts } from '@components/delaerVisit';
import { ProductImages } from '@components/siteLeads/components/siteProducts/productImages';
import { ToastType, useTosterStore } from '@store/toster';
import { Modal } from '@components/common/modal/modal';
import { QuickAddEndUser } from '../quickAddEndUser';
import { TimePicker } from '@components/common/time-picker/time-picker';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useValidation } from '@hooks/validations';
import { useFormError } from '@hooks/useFormError';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	useGetOneToOneMeetingsList,
	useOneToOneMeetingMutation,
	useRetrieveMeetingDetails,
} from '@services/hooks/oneToOneMeetings';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { IEndUserList, IOneToOneMeetingsList, IWSSTerritoryList } from '@components/onetoonemeetings';
import {
	convertTime,
	extractTime,
	formatDate,
	formatDateString,
	generateTimeOptions,
	getTownInfo,
} from '@helper/utils';
import { useGeoLocationDetails } from '@hooks/useGeoLocationDetails';
import { usePresignedURLQuery } from '@services/hooks/enduser-details/usePresignedURLQuery';
import { useUploadSiteImagesMutation } from '@services/hooks/site-leads/useUploadSiteImagesMutation';
import { useDeletePresignedURL } from '@services/hooks/enduser-details/useDeletePresignedURL';
import { convertUrlToFile } from '@components/delaerVisit/components/addEditDealerVisit';
import { useImageUpload } from '@hooks/useImageUpload';
import { QrModal } from '@components/dashboard/components/meeting/qrModal';
import { ConfirmationModal } from '@components/conductedMeetings/confirmationModal/confirmationModal';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { SiteLeadConfigurations } from '@components/siteLeads';
import { useIsImpersonationLoginUser } from '../../../../authGuard/hooks/useIsImpersonationLoginUser';

export const AddEditOneToOneMeetings = (): ReactElement => {
	useSetMainHeader('One To One Connect');
	const { state } = useLocation();
	const navigate = useNavigate();
	const { setToaster } = useTosterStore();
	const { OneToOneMeetingValidationSchema } = useValidation();
	const [isQuickEndModalOpen, setIsQuickEndModalOpen] = useState(false);
	const { result: towns } = useGetRxDb<TownList>('towns');
	const { result: visitProducts } = useGetRxDb<IDealerVisitProducts>('dealer_visit_products');
	const { result: endUserList } = useGetRxDb<IEndUserList>('end_user_list');
	const { result: wssTerritoryList } = useGetRxDb<IWSSTerritoryList>('wss_territory_list');
	const { refetch: refetchOneToOneMeetingList } = useGetOneToOneMeetingsList();
	const { coords } = useGeoLocationDetails();
	const { handleAddImage } = useImageUpload();
	const {
		data: retrievedMeetingDetails,
		isFetching: isFetchingMeeting,
		error: errorFetchingMeeting,
	} = useRetrieveMeetingDetails(state?.meetingId);
	const { mutate: createUpdateOneToOneMeeting, isPending: isMeetingPending } = useOneToOneMeetingMutation(
		state?.meetingId
	);
	const { mutate: mutatePresignedURL, data: getSiteImagesPresignedUrl } = usePresignedURLQuery();
	const { mutate: putSiteImagesData, isPending: isPutSiteImagePending } =
		useUploadSiteImagesMutation(getSiteImagesPresignedUrl);
	const { mutate: deletePresignedURL } = useDeletePresignedURL();

	const [openQrModal, setOpenQrModal] = useState<boolean>(false);
	const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
	const [meetingId, setMeetingId] = useState<number | undefined>(undefined);
	const [originalImageUrls, setOriginalImageUrls] = useState<(string | null)[]>([null, null]);
	const [newImages, setNewImages] = useState<File[]>([]);
	const [newImageIndices, setNewImageIndices] = useState<number[]>([]);
	const [removedImageIndices, setRemovedImageIndices] = useState<number[]>([]);
	const [deletedImageUrls, setDeletedImageUrls] = useState<string[]>([]);
	const fileToUrlMapRef = useRef<Map<File, string>>(new Map());
	const prevProductImagesRef = useRef<File[]>([]);
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const formik = useFormik({
		initialValues: oneToOneMeetingFormInitialValues,
		onSubmit: () => {},
		validationSchema: OneToOneMeetingValidationSchema,
	});
	const getErrorMessage = useFormError<IOneToOneMeetingFormInitialValues>(formik);

	const townsList = useMemo((): DropdownOptions[] => {
		if (towns?.length) {
			return towns.map((t) => ({
				id: t?.id,
				value: `${t?.towncode_c} - ${t?.towndescription_c}`,
			}));
		}
		return [];
	}, [towns]);

	const productsList = useMemo((): DropdownOptions[] => {
		if (visitProducts?.length) {
			return [...visitProducts]
				.sort((a, b) => a.productgroup3description_c.localeCompare(b.productgroup3description_c))
				.map((d, index) => ({
					id: index,
					value: d.productgroup3description_c,
					name: d.prdgroup3_c,
				}));
		}
		return [];
	}, [visitProducts]);

	const endUsers = useMemo((): DropdownOptions[] => {
		if (endUserList?.length) {
			return endUserList.map((d) => ({
				id: d?.id,
				value: `${d?.name} - ${d?.phone}`,
			}));
		}
		return [];
	}, [endUserList]);

	const wssTerritory = useMemo((): DropdownOptions[] => {
		if (wssTerritoryList?.length) {
			return wssTerritoryList.map((d, index) => ({
				id: index,
				value: `${d?.wssterritorycode_c} - ${d?.wssterritoryname_c}`,
				name: d?.wssterritoryname_c,
			}));
		}
		return [];
	}, [wssTerritoryList]);

	const loadImages = async (event_image_1?: string | null, event_image_2?: string | null): Promise<void> => {
		const imageUrls = [];
		if (event_image_1) imageUrls.push(event_image_1);
		if (event_image_2) imageUrls.push(event_image_2);

		const promises = imageUrls.map((url) => convertUrlToFile(url));
		const filesResult = await Promise.all(promises);
		const files = filesResult.filter((f): f is File => f !== null);
		if (files.length > 0) {
			formik.setFieldValue('productImages', files);

			setOriginalImageUrls([event_image_1 ?? null, event_image_2 ?? null]);
			setNewImages([]);
			setNewImageIndices([]);
			setRemovedImageIndices([]);
			setDeletedImageUrls([]);
			fileToUrlMapRef.current = new Map();
			files.forEach((file, index) => {
				const url = index === 0 ? event_image_1 : event_image_2;
				if (url && file) {
					fileToUrlMapRef.current.set(file, url);
				}
			});
			prevProductImagesRef.current = files;
		}
	};

	const handleImageAdd = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const maxImageLimit = 2;
			const folderName = 'one_to_one_meetings';
			const result = handleAddImage(event, formik.values.productImages, maxFileSize, maxImageLimit, folderName);
			if (result) {
				const { updatedImages } = result;
				if (updatedImages) {
					formik.setFieldValue('productImages', updatedImages);
					const newFiles: File[] = [];
					const newIndices: number[] = [];

					updatedImages.forEach((file, index) => {
						if (!fileToUrlMapRef.current.has(file)) {
							newFiles.push(file);
							newIndices.push(index);
						}
					});

					if (newFiles.length > 0) {
						const newPayload = newFiles.map((img) => ({
							folder: folderName,
							filename: img.name,
						}));
						setNewImages(newFiles);
						setNewImageIndices(newIndices);
						if (newPayload.length > 0) {
							mutatePresignedURL?.(newPayload);
						}
					}
				}
			}
		},
		[formik, handleAddImage, mutatePresignedURL]
	);

	const handleQuickEndUser = (): void => {
		setIsQuickEndModalOpen(true);
	};

	const redirectionToList = useCallback((): void => {
		setToaster(true, ToastType.success, `One to one meeting ${state?.meetingId ? 'updated' : 'created'} successfully`);
		navigate('/one-to-one-meeting-summary', {
			replace: true,
		});
	}, [setToaster, state?.meetingId, navigate]);

	const deleteRemovedImages = useCallback((): void => {
		if (deletedImageUrls.length > 0) {
			deletedImageUrls.forEach((url) => {
				deletePresignedURL(
					{ url },
					{
						onSuccess: () => {},
						onError: () => {},
					}
				);
			});
			setDeletedImageUrls([]);
		}
	}, [deletedImageUrls, deletePresignedURL]);

	const handleSubmit = useCallback((): void => {
		const townInfo = getTownInfo(towns, formik.values.town);

		const imagesToUpload: File[] = [];

		if (!state?.meetingId) {
			imagesToUpload.push(...formik.values.productImages);
		} else {
			imagesToUpload.push(...newImages);
		}

		const buildFinalUrls = (presignedUrls?: typeof getSiteImagesPresignedUrl): (string | null)[] => {
			const finalUrls: (string | null)[] = [null, null];
			let presignedIndex = 0;

			if (!state?.meetingId) {
				if (presignedUrls?.length) {
					formik.values.productImages.forEach((_, index) => {
						if (index < presignedUrls.length) {
							finalUrls[index] = presignedUrls[index]?.normal_url ?? null;
						}
					});
				}
			} else {
				const filledRemovedPositions = new Set<number>();

				formik.values.productImages.forEach((currentFile, arrayIndex) => {
					const fileUrl = fileToUrlMapRef.current.get(currentFile);

					if (fileUrl) {
						const originalIndex = originalImageUrls.findIndex((url) => url === fileUrl);
						if (originalIndex !== -1 && !removedImageIndices.includes(originalIndex)) {
							finalUrls[originalIndex] = fileUrl;
						}
					} else if (newImageIndices.includes(arrayIndex)) {
						let targetPosition = -1;

						if (removedImageIndices.length > 0 && presignedIndex < removedImageIndices.length) {
							targetPosition = removedImageIndices[presignedIndex];
							filledRemovedPositions.add(targetPosition);
						} else {
							const emptyPosition = finalUrls.findIndex((url) => url === null);
							if (emptyPosition !== -1) {
								targetPosition = emptyPosition;
							}
						}

						if (targetPosition !== -1 && targetPosition < 2 && presignedUrls?.[presignedIndex]) {
							finalUrls[targetPosition] = presignedUrls[presignedIndex]?.normal_url ?? null;
							presignedIndex++;
						}
					}
				});

				removedImageIndices.forEach((index) => {
					if (!filledRemovedPositions.has(index)) {
						finalUrls[index] = null;
					}
				});
			}

			return finalUrls;
		};

		if (imagesToUpload.length === 0) {
			const finalUrls = buildFinalUrls();

			const payload: IOneToOneMeetingsList = {
				town_code_c: townInfo?.towncode ?? '',
				town_name_c: townInfo?.townname ?? '',
				mobile_no_c: formik.values.endUser?.split('-')[1]?.trim(),
				wss_teritorry_code_c: formik.values.wssTerritory.split('-')[0]?.trim(),
				wss_teritorry_name_c: wssTerritory?.find((p) => p.value.includes(formik.values.wssTerritory))?.name,
				product1_demoed_code_c: productsList?.find((p) => p.value.includes(formik.values.Product1Demo))?.name,
				product1_demoed_c: formik.values.Product1Demo,
				product2_demoed_code_c: productsList?.find((p) => p.value.includes(formik.values.Product2Demo))?.name,
				product2_demoed_c: formik.values.Product2Demo,
				activities_done_at_site_c: formik.values.typeOfActivity,
				other_suggestions_c: formik.values.typeOfActivity === 'Others' ? formik.values.otherTypeOfActivity : null,
				user_name_c: formik.values.endUser?.split('-')[0]?.trim(),
				startdatetime: formatDateTime(
					`${formatDate(new Date(formik.values.meetingDate))}${' '}
					${
						generateTimeOptions()
							.find((item) => item.value === formik.values.meetingTime)
							?.id.toString() ?? ''
					}`
				),
				subject: 'One to One Meeting',
				event_image_1: finalUrls[0] ?? null,
				event_image_2: finalUrls[1] ?? null,
				longitude: coords.longitude,
				latitude: coords.latitude,
			};

			createUpdateOneToOneMeeting?.(payload, {
				onSuccess: (response) => {
					if (state?.meetingId) {
						deleteRemovedImages();
					}
					refetchOneToOneMeetingList?.();
					if (state?.meetingId) {
						redirectionToList();
						return;
					}
					setMeetingId(response.id);
					if (SiteLeadConfigurations.QR_FLOW) {
						setOpenQrModal(true);
						return;
					}
					redirectionToList();
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message ?? 'Failed to create One to one meeting!');
				},
			});
			return;
		}

		// Upload only new/updated images
		putSiteImagesData?.(imagesToUpload, {
			onSuccess: () => {
				const finalUrls = buildFinalUrls(getSiteImagesPresignedUrl);

				const payload: IOneToOneMeetingsList = {
					town_code_c: townInfo?.towncode ?? '',
					town_name_c: townInfo?.townname ?? '',
					mobile_no_c: formik.values.endUser?.split('-')[1]?.trim(),
					wss_teritorry_code_c: formik.values.wssTerritory.split('-')[0]?.trim(),
					wss_teritorry_name_c: wssTerritory?.find((p) => p.value.includes(formik.values.wssTerritory))?.name,
					product1_demoed_code_c: productsList?.find((p) => p.value.includes(formik.values.Product1Demo))?.name,
					product1_demoed_c: formik.values.Product1Demo,
					product2_demoed_code_c: productsList?.find((p) => p.value.includes(formik.values.Product2Demo))?.name,
					product2_demoed_c: formik.values.Product2Demo,
					activities_done_at_site_c: formik.values.typeOfActivity,
					other_suggestions_c: formik.values.typeOfActivity === 'Others' ? formik.values.otherTypeOfActivity : null,
					user_name_c: formik.values.endUser?.split('-')[0]?.trim(),
					startdatetime: formatDateTime(
						`${formatDate(new Date(formik.values.meetingDate))}${' '}
						${
							generateTimeOptions()
								.find((item) => item.value === formik.values.meetingTime)
								?.id.toString() ?? ''
						}`
					),
					subject: 'One to One Meeting',
					event_image_1: finalUrls[0] ?? null,
					event_image_2: finalUrls[1] ?? null,
					longitude: coords.longitude,
					latitude: coords.latitude,
				};

				createUpdateOneToOneMeeting?.(payload, {
					onSuccess: (response) => {
						if (state?.meetingId) {
							deleteRemovedImages();
						}
						refetchOneToOneMeetingList?.();
						if (state?.meetingId) {
							redirectionToList();
							return;
						}
						setMeetingId(response.id);
						if (SiteLeadConfigurations.QR_FLOW) {
							setOpenQrModal(true);
							return;
						}
						redirectionToList();
					},
					onError: (error) => {
						setToaster(true, ToastType.error, error?.message ?? 'Failed to create One to one meeting!');
					},
				});
			},
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values,
		getSiteImagesPresignedUrl,
		wssTerritory,
		productsList,
		coords,
		createUpdateOneToOneMeeting,
		state,
		refetchOneToOneMeetingList,
		putSiteImagesData,
		originalImageUrls,
		newImages,
		newImageIndices,
		removedImageIndices,
		deleteRemovedImages,
	]);

	useEffect(() => {
		if (state?.meetingId && retrievedMeetingDetails) {
			const { event_image_1, event_image_2 } = retrievedMeetingDetails;
			loadImages(event_image_1, event_image_2);
			formik.setValues(
				{
					...formik.values,
					endUser: `${retrievedMeetingDetails?.user_name_c} - ${retrievedMeetingDetails?.mobile_no_c}`,
					wssTerritory:
						retrievedMeetingDetails?.wss_teritorry_code_c && retrievedMeetingDetails?.wss_teritorry_name_c
							? `${retrievedMeetingDetails?.wss_teritorry_code_c} - ${retrievedMeetingDetails?.wss_teritorry_name_c}`
							: '',
					Product1Demo: retrievedMeetingDetails?.product1_demoed_c ?? '',
					Product2Demo: retrievedMeetingDetails?.product2_demoed_c ?? '',
					town:
						retrievedMeetingDetails?.town_code_c && retrievedMeetingDetails?.town_name_c
							? `${retrievedMeetingDetails?.town_code_c} - ${retrievedMeetingDetails?.town_name_c}`
							: '',
					typeOfActivity: retrievedMeetingDetails?.activities_done_at_site_c,
					otherTypeOfActivity: retrievedMeetingDetails?.other_suggestions_c,
					meetingDate: retrievedMeetingDetails?.startdatetime
						? formatDateString(retrievedMeetingDetails.startdatetime, false)
						: '',
					meetingTime: retrievedMeetingDetails?.startdatetime
						? convertTime(extractTime(retrievedMeetingDetails?.startdatetime))
						: '',
				},
				true
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state?.meetingId, retrievedMeetingDetails]);

	useEffect(() => {
		if (state?.meetingId && originalImageUrls.some((url) => url !== null)) {
			const currentImages = formik.values.productImages || [];
			const previousImages = prevProductImagesRef.current || [];

			if (currentImages.length < previousImages.length) {
				const getFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`;
				const currentFilesKeys = new Set(currentImages.map(getFileKey));

				const removedFiles: File[] = [];
				previousImages.forEach((prevFile) => {
					const prevFileKey = getFileKey(prevFile);
					if (!currentFilesKeys.has(prevFileKey)) {
						removedFiles.push(prevFile);
					}
				});

				removedFiles.forEach((removedFile) => {
					const removedUrl = fileToUrlMapRef.current.get(removedFile);
					if (removedUrl) {
						const removedIndex = originalImageUrls.findIndex((url) => url === removedUrl);
						if (removedIndex !== -1) {
							setRemovedImageIndices((prev) => {
								if (!prev.includes(removedIndex)) {
									return [...prev, removedIndex];
								}
								return prev;
							});
							setDeletedImageUrls((prev) => {
								if (!prev.includes(removedUrl)) {
									return [...prev, removedUrl];
								}
								return prev;
							});
						}
						fileToUrlMapRef.current.delete(removedFile);
					}
				});

				setNewImages((prevNewImages) => {
					const getFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`;
					const remainingNewImages = prevNewImages.filter((newFile) => {
						return currentImages.some((currentFile) => getFileKey(currentFile) === getFileKey(newFile));
					});

					if (remainingNewImages.length !== prevNewImages.length) {
						const remainingIndices: number[] = [];
						remainingNewImages.forEach((newFile) => {
							const index = currentImages.findIndex((currentFile) => getFileKey(currentFile) === getFileKey(newFile));
							if (index !== -1) {
								remainingIndices.push(index);
							}
						});
						setNewImageIndices(remainingIndices);
						return remainingNewImages;
					}
					return prevNewImages;
				});
			} else if (currentImages.length >= previousImages.length) {
				setNewImageIndices((prevIndices) => {
					const updatedIndices: number[] = [];
					currentImages.forEach((file, index) => {
						if (!fileToUrlMapRef.current.has(file)) {
							updatedIndices.push(index);
						}
					});
					return updatedIndices.length > 0 ? updatedIndices : prevIndices;
				});
			}
		}

		prevProductImagesRef.current = formik.values.productImages || [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.productImages, state?.meetingId, originalImageUrls]);

	const handleModalClose = useCallback(() => {
		setOpenQrModal(false);
		setConfirmationModal(true);
	}, []);

	const handleConfirmationModalClose = useCallback(() => {
		setConfirmationModal(false);
		setOpenQrModal(true);
	}, []);

	const handleConfirmSubmit = useCallback(() => {
		overlayRemoveFadeAction(() => {});
		setConfirmationModal(false);
		setOpenQrModal(false);
		redirectionToList();
	}, [redirectionToList]);

	return (
		<>
			<section className="add-one-to-one-meeting">
				<Wrapper isLoaderVisible={isFetchingMeeting} isError={errorFetchingMeeting}>
					<div className="container">
						<form>
							<ul className="row">
								<li className="form-control d-col d-col-2">
									<TextInput
										id="oneToOneConnect"
										name="oneToOneConnect"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.oneToOneConnect}
										label="One To One Connect"
										setFieldValue={formik.setFieldValue}
										error={getErrorMessage('oneToOneConnect')}
										disabled
									/>
								</li>
								<li className="d-col d-col-2 form-control">
									<Dropdown
										id="endUser"
										name="endUser"
										label="End User"
										onBlur={formik.handleBlur}
										title={formik.values.endUser ?? ''}
										error={getErrorMessage('endUser')}
										options={endUsers}
										setFieldValue={formik.setFieldValue}
										blurValidate
										required
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<Dropdown
										id="wssTerritory"
										name="wssTerritory"
										label="WSS Territory"
										onBlur={formik.handleBlur}
										title={formik.values.wssTerritory ?? ''}
										error={getErrorMessage('wssTerritory')}
										options={wssTerritory}
										setFieldValue={formik.setFieldValue}
										blurValidate
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<Dropdown
										id="town"
										name="town"
										label="Town"
										onBlur={formik.handleBlur}
										title={formik.values.town ?? ''}
										error={getErrorMessage('town')}
										options={townsList}
										setFieldValue={formik.setFieldValue}
										blurValidate
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<SingleDatePicker
										className="single-date-picker"
										id="meetingDate"
										name="meetingDate"
										value={formik.values.meetingDate}
										label="Meeting Date"
										formik={formik}
										error={getErrorMessage('meetingDate')}
										required
										maxDate={new Date()}
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<TimePicker
										name="meetingTime"
										id="meetingTime"
										label="Meeting Time"
										value={formik.values.meetingTime}
										formik={formik}
										icon={'icon-clock'}
										error={getErrorMessage('meetingTime')}
										required
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<Dropdown
										id="typeOfActivity"
										name="typeOfActivity"
										label="Type of Activity"
										onBlur={formik.handleBlur}
										title={formik.values.typeOfActivity ?? ''}
										error={getErrorMessage('typeOfActivity')}
										options={typeOfActivitiesList}
										setFieldValue={formik.setFieldValue}
										blurValidate
										required
										disabled={isImpersonationLoginUser}
									/>
								</li>
								{formik.values.typeOfActivity === 'Others' && (
									<li className="form-control d-col d-col-2">
										<TextInput
											id="otherTypeOfActivity"
											name="otherTypeOfActivity"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.otherTypeOfActivity ?? ''}
											label="Other Type of Activity"
											setFieldValue={formik.setFieldValue}
											error={getErrorMessage('otherTypeOfActivity')}
											required
											disabled={isImpersonationLoginUser}
										/>
									</li>
								)}
								<li className="d-col d-col-2">
									<Dropdown
										id="Product1Demo"
										name="Product1Demo"
										label="Product 1 Demo"
										onBlur={formik.handleBlur}
										title={formik.values.Product1Demo ?? ''}
										error={getErrorMessage('Product1Demo')}
										options={productsList}
										setFieldValue={formik.setFieldValue}
										blurValidate
										required
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<Dropdown
										id="Product2Demo"
										name="Product2Demo"
										label="Product 2 Demo"
										onBlur={formik.handleBlur}
										title={formik.values.Product2Demo ?? ''}
										error={getErrorMessage('Product2Demo')}
										options={productsList}
										setFieldValue={formik.setFieldValue}
										blurValidate
										disabled={isImpersonationLoginUser}
									/>
								</li>
								<li className="d-col d-col-2">
									<ProductImages
										fieldName="productImages"
										formik={formik}
										handleAddImage={handleImageAdd}
										showAddButton={!isImpersonationLoginUser}
										required
									/>
								</li>
								<li className="d-col d-col-2">
									<div className={`manage-participants-button`}>
										<div className="label">
											<span className="content">Referral Leads</span>
										</div>
										<div className="location-wrapper">
											<div className="location-icons">
												<button
													className="location-btn"
													aria-label="location map link"
													type="button"
													onClick={handleQuickEndUser}
													disabled={isImpersonationLoginUser}
												>
													<span className="icon-plus"></span>
												</button>
											</div>
										</div>
									</div>
								</li>
							</ul>
						</form>
						<SubmitButton
							label="Submit"
							isDisabled={
								isImpersonationLoginUser ||
								!formik.isValid ||
								!formik.dirty ||
								isPutSiteImagePending ||
								!formik.values.productImages.length
							}
							isPending={isMeetingPending || isPutSiteImagePending}
							handleSubmitClick={handleSubmit}
						/>
					</div>
				</Wrapper>
			</section>
			<Modal
				modalId="quickAddEndUser"
				className="dialog dialog-lg  dialog-bottom qr-scan-wrapper quick-add-user-pop"
				modalOpen={isQuickEndModalOpen}
				onModalClose={() => {
					overlayRemoveFadeAction(() => setIsQuickEndModalOpen(false));
				}}
			>
				<QuickAddEndUser setIsQuickEndModalOpen={setIsQuickEndModalOpen} />
			</Modal>
			{openQrModal && (
				<Modal
					modalId="siteQrCode"
					className="dialog-sm site-lead-qr-code dialog-bottom"
					modalOpen={openQrModal}
					isEscAllow={false}
					onModalClose={handleModalClose}
					modalTitle={'Scan QR Code To Verify Site'}
				>
					<QrModal qrId={meetingId?.toString()} modalClose={handleModalClose} isCloseVisible={false} />
				</Modal>
			)}
			{confirmationModal && (
				<Modal
					modalId="confirmationModal"
					className="dialog-sm site-lead-confrim"
					modalOpen={confirmationModal}
					isEscAllow={false}
					onModalClose={handleConfirmationModalClose}
				>
					<ConfirmationModal
						onClose={handleConfirmationModalClose}
						messageClassName={'h3'}
						onSubmit={handleConfirmSubmit}
						message="Are you sure you do not want to verify this site?"
						buttonLabel={'Confirm'}
					/>
				</Modal>
			)}
		</>
	);
};
